import SearchAPI from '../../api/search';
import types from '../mutation-types';
export const initialState = {
  query: '',
  records: [],
  contactRecords: [],
  conversationRecords: [],
  messageRecords: [],
  articleRecords: [],
  agentRecords: [],
  uiFlags: {
    isFetching: false,
    isSearchCompleted: false,
    contact: { isFetching: false },
    conversation: { isFetching: false },
    message: { isFetching: false },
    article: { isFetching: false },
    agent: { isFetching: false },
  },
};

export const getters = {
  getConversations(state) {
    return state.records;
  },
  getContactRecords(state) {
    return state.contactRecords;
  },
  getConversationRecords(state) {
    return state.conversationRecords;
  },
  getMessageRecords(state) {
    return state.messageRecords;
  },
  getArticleRecords(state) {
    return state.articleRecords;
  },
  getAgentRecords(state) {
    return state.agentRecords;
  },
  getUIFlags(state) {
    return state.uiFlags;
  },
};

export const actions = {
  async get({ commit }, { q }) {
    commit(types.SEARCH_CONVERSATIONS_SET, []);
    if (!q) {
      return;
    }
    commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload },
      } = await SearchAPI.get({ q });
      commit(types.SEARCH_CONVERSATIONS_SET, payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  async fullSearch({ commit, dispatch, state }, { q }) {
    if (!q) {
      return;
    }
    commit(types.SET_SEARCH_QUERY, q);
    commit(types.FULL_SEARCH_SET_UI_FLAG, {
      isFetching: true,
      isSearchCompleted: false,
    });
    try {
      await Promise.all([
        dispatch('contactSearch', { q }),
        dispatch('conversationSearch', { q }),
        dispatch('messageSearch', { q }),
        dispatch('articleSearch', { q }),
        dispatch('agentSearch', { q }),
      ]);
    } catch (error) {
      // Ignore error
    } finally {
      if (!state || q === state.query) {
        commit(types.FULL_SEARCH_SET_UI_FLAG, {
          isFetching: false,
          isSearchCompleted: true,
        });
      }
    }
  },
  async contactSearch({ commit, state }, { q, page = 1 }) {
    commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.contacts({ q, page });
      if (state && q !== state.query) return;
      commit(types.CONTACT_SEARCH_SET, data.payload.contacts);
    } catch (error) {
      // Ignore error
    } finally {
      if (!state || q === state.query) {
        commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: false });
      }
    }
  },
  async conversationSearch({ commit, state }, { q, page = 1 }) {
    commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.conversations({ q, page });
      if (state && q !== state.query) return;
      commit(types.CONVERSATION_SEARCH_SET, data.payload.conversations);
    } catch (error) {
      // Ignore error
    } finally {
      if (!state || q === state.query) {
        commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: false });
      }
    }
  },
  async messageSearch({ commit, state }, { q, page = 1 }) {
    commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.messages({ q, page });
      if (state && q !== state.query) return;
      commit(types.MESSAGE_SEARCH_SET, data.payload.messages);
    } catch (error) {
      // Ignore error
    } finally {
      if (!state || q === state.query) {
        commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: false });
      }
    }
  },
  async articleSearch({ commit, state }, { q, page = 1 }) {
    commit(types.ARTICLE_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.articles({ q, page });
      if (state && q !== state.query) return;
      commit(types.ARTICLE_SEARCH_SET, data.payload.articles);
    } catch (error) {
      // Ignore error
    } finally {
      if (!state || q === state.query) {
        commit(types.ARTICLE_SEARCH_SET_UI_FLAG, { isFetching: false });
      }
    }
  },
  async agentSearch({ commit, state }, { q, page = 1 }) {
    commit(types.AGENT_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.agents({ q, page });
      if (state && q !== state.query) return;
      commit(types.AGENT_SEARCH_SET, data.payload.agents);
    } catch (error) {
      // Ignore error
    } finally {
      if (!state || q === state.query) {
        commit(types.AGENT_SEARCH_SET_UI_FLAG, { isFetching: false });
      }
    }
  },
  async clearSearchResults({ commit }) {
    commit(types.CLEAR_SEARCH_RESULTS);
  },
};

export const mutations = {
  [types.SET_SEARCH_QUERY](state, query) {
    state.query = query;
  },
  [types.SEARCH_CONVERSATIONS_SET](state, records) {
    state.records = records;
  },
  [types.CONTACT_SEARCH_SET](state, records) {
    state.contactRecords = [...state.contactRecords, ...records];
  },
  [types.CONVERSATION_SEARCH_SET](state, records) {
    state.conversationRecords = [...state.conversationRecords, ...records];
  },
  [types.MESSAGE_SEARCH_SET](state, records) {
    state.messageRecords = [...state.messageRecords, ...records];
  },
  [types.ARTICLE_SEARCH_SET](state, records) {
    state.articleRecords = [...state.articleRecords, ...records];
  },
  [types.AGENT_SEARCH_SET](state, records) {
    state.agentRecords = [...state.agentRecords, ...records];
  },
  [types.SEARCH_CONVERSATIONS_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.FULL_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.CONTACT_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.contact = { ...state.uiFlags.contact, ...uiFlags };
  },
  [types.CONVERSATION_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.conversation = { ...state.uiFlags.conversation, ...uiFlags };
  },
  [types.MESSAGE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.message = { ...state.uiFlags.message, ...uiFlags };
  },
  [types.ARTICLE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.article = { ...state.uiFlags.article, ...uiFlags };
  },
  [types.AGENT_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.agent = { ...state.uiFlags.agent, ...uiFlags };
  },
  [types.CLEAR_SEARCH_RESULTS](state) {
    state.query = '';
    state.contactRecords = [];
    state.conversationRecords = [];
    state.messageRecords = [];
    state.articleRecords = [];
    state.agentRecords = [];
    state.uiFlags = {
      isFetching: false,
      isSearchCompleted: false,
      contact: { isFetching: false },
      conversation: { isFetching: false },
      message: { isFetching: false },
      article: { isFetching: false },
      agent: { isFetching: false },
    };
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
